import React from 'react';
import { TransitionMotion, spring } from 'react-motion';
import { preloadImg } from 'helpers';
import { CSSTransition, Transition } from 'react-transition-group';
import { NodeGroup } from 'react-move';
import { easeCubicIn } from 'd3-ease';
import cx from 'classnames';
import T from 'prop-types';
import * as Modules from 'components/modules';
import styles from './stackCarousel.css';

const initializeDataArray = ({ dataArray, cardsQuantity }) => {
  const sideCardsQuantity = Math.floor(cardsQuantity / 2);
  const indexedArray = dataArray.map((item, index) =>
    Object.assign(item, { _index: `${index}` }));

  const shiftedArray = [
    ...indexedArray.slice(-sideCardsQuantity),
    indexedArray[0],
    ...indexedArray.slice(1, -sideCardsQuantity),
  ];

  return shiftedArray.reverse();
};

class Slider extends React.Component {
  state = {
    cards: initializeDataArray(this.props),
    index: 0,
  };

  componentDidMount() {
    this.props.dataArray.forEach(item => preloadImg(item.src));
  }

  prevSlide = () => {
    const newCardsArray = [...this.state.cards.slice(1), this.state.cards[0]];

    this.setState(
      {
        cards: newCardsArray,
        index: +newCardsArray[0]._index,
      },
      () => this.props.indexCb(this.state.index),
    );
  };

  nextSlide = () => {
    const newCardsArray = [
      ...this.state.cards.slice(-1),
      ...this.state.cards.slice(0, -1),
    ];

    this.setState(
      {
        cards: newCardsArray,
        index: +newCardsArray[0]._index,
      },
      () => this.props.indexCb(this.state.index),
    );
  };

  openSlider = () => {
    !this.props.active && this.props.toggleSliderCb();
  };
  closeSlider = () => {
    this.props.active && this.props.toggleSliderCb();
  };
  arrowRightHandler = () => {
    this.props.active && this.nextSlide();
  };
  arrowLeftHandler = () => {
    this.props.active && this.prevSlide();
  };

  render() {
    const {
      Card,
      className,
      active,
      overlayColor,
      xOffetsArray,
      scalesArray,
      zIndexArray,
      opacityArray,
      intoView,
      cardsQuantity,
      topFrame,
      bottomFrame,
    } = this.props;

    const { cards } = this.state;
    return (
      <CSSTransition
        in={active}
        timeout={{ enter: 500, exit: 800 }}
        classNames={{
          enter: styles.enter,
          enterActive: styles.enterActive,
          exit: styles.exit,
          exitActive: styles.exitActive,
        }}
      >
        <div>
          <NodeGroup
            data={cards.slice(-cardsQuantity)}
            keyAccessor={d => d._index}
            start={(data, index) => ({
              x: xOffetsArray[index],
              s: scalesArray[index],
              z: zIndexArray[index],
              o: opacityArray[index],
            })}
            enter={(data, index) => ({
              x: [xOffetsArray[index]],
              s: [scalesArray[index]],
              o: opacityArray[index],
              z: zIndexArray[index],
              timing: { duration: 0 },
            })}
            leave={(data, index) => ({
              x: [xOffetsArray[index]],
              s: [scalesArray[index]],
              o: opacityArray[index],
              z: -1,
              timing: { duration: 0 },
            })}
            update={(data, index) => [
              {
                x: [xOffetsArray[index]],
                s: [scalesArray[index]],
                z: zIndexArray[index],
                o: [opacityArray[index]],

                timing: this.props,
              },
            ]}
          >
            {interpolated => (
              <div className={cx(styles.root, className)}>
                {interpolated.map(({ key, state, data }) => (
                  <div
                    role="presentation"
                    className={cx(active ? styles.cardActive : styles.cardInactive)}
                    key={key}
                    style={{
                      transform: `translate3d(${state.x}%, -50%, 0) scale(${state.s})`,
                      zIndex: `${state.z}`,
                    }}
                  >
                    <div
                      style={{
                        backgroundColor: `${overlayColor}`,
                        opacity: `${active ? state.o : 0}`,
                      }}
                      className={styles.overlay}
                    />
                    <Card {...data} />
                  </div>
                ))}
                {topFrame}
                {bottomFrame}
                <button
                  onClick={this.nextSlide}
                  title="Следующий слайд"
                  className={styles.nextSlideButton}
                />
                <button
                  onClick={this.prevSlide}
                  title="Предыдущий слайд"
                  className={styles.prevSlideButton}
                />
              </div>
            )}
          </NodeGroup>
          <Modules.SectionKeyHandlers
            arrowRightHandler={this.arrowRightHandler}
            arrowLeftHandler={this.arrowLeftHandler}
            enterHandler={this.openSlider}
            escHandler={this.closeSlider}
            active={intoView}
          />
        </div>
      </CSSTransition>
    );
  }
}

Slider.defaultProps = {
  className: null,
  cardsQuantity: 3,
  scalesArray: [0.9, 1, 0.9],
  xOffetsArray: [-30, -50, -70],
  zIndexArray: [1, 2, 1],
  opacityArray: [0.7, 0, 0.7],
  yCenter: -50,
  xCenter: -50,
  xAmplitude: 40,
  overlayColor: null,
  indexCb: () => {},
  toggleSliderCb: () => {},
  timing: { duration: 300, ease: easeCubicIn },
};

Slider.propTypes = {
  Card: T.oneOfType([T.element, T.func]).isRequired,
  className: T.string,
  dataArray: T.array.isRequired,
  cardsQuantity: T.number,
  scalesArray: T.arrayOf(T.number), // scales for 2nd and etc cards
  xOffetsArray: T.arrayOf(T.number), // scales for 2nd and etc cards
  zIndexArray: T.arrayOf(T.number), // scales for 2nd and etc cards
  opacityArray: T.arrayOf(T.number), // scales for 2nd and etc cards
  yCenter: T.number, // transform x coordinate of first card
  xCenter: T.number, // transform y coordinate of first card
  xAmplitude: T.number, // amplitude of first card movement
  overlayColor: T.string,
  toggleSliderCb: T.func,
  intoView: T.bool.isRequired,
  indexCb: T.func,
  active: T.bool.isRequired,
  timing: T.shape({ duration: T.number, ease: T.func }),
};

export default Slider;



// WEBPACK FOOTER //
// ./src/components/modules/StackCarousel/StackCarousel.js